/**
 * The mark of a support session (DOP-562): a red frame around the whole app
 * and a pill saying who is viewing as whom, how long is left, and Exit.
 *
 * It cannot be dismissed, and it is drawn from the SERVER's account of the
 * session — not from the token in this tab — so what it says is what the
 * audit record says. A frame plus a pill rather than a bar: the app's pages
 * are laid out to the full viewport height, and a bar would push every one of
 * them off the bottom of the screen.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
	endSupportSession,
	getSupportSession,
	SupportSession,
} from "../services/supportApi";
import { SUPPORT_ENDED_PATH } from "../utils/sessionExpiry";
import {
	clearSupportToken,
	isSupportSession,
	rememberConsoleUrl,
} from "../utils/sessionToken";

const formatRemaining = (ms: number): string => {
	const total = Math.max(0, Math.floor(ms / 1000));
	const minutes = Math.floor(total / 60);
	const seconds = total % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const leave = (): void => {
	clearSupportToken();
	window.location.replace(SUPPORT_ENDED_PATH);
};

const SupportSessionBanner: React.FC = () => {
	const active = isSupportSession();
	const [session, setSession] = useState<SupportSession | null>(null);
	const [now, setNow] = useState(() => Date.now());
	const [exiting, setExiting] = useState(false);

	useEffect(() => {
		if (!active) return;
		let cancelled = false;
		getSupportSession()
			.then((loaded) => {
				if (cancelled) return;
				setSession(loaded);
				rememberConsoleUrl(loaded.console_url);
			})
			.catch(() => {
				// A dead session 401s and apiFetch has already sent the tab to
				// /support/ended. Anything else: the frame still shows, unnamed.
			});
		return () => {
			cancelled = true;
		};
	}, [active]);

	useEffect(() => {
		if (!active) return;
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, [active]);

	const remaining = session ? session.expires_at - now : null;
	useEffect(() => {
		if (remaining !== null && remaining <= 0) leave();
	}, [remaining]);

	const exit = useCallback(async () => {
		setExiting(true);
		try {
			await endSupportSession();
		} catch {
			// The server may already have ended it. Either way this tab is done.
		}
		leave();
	}, []);

	if (!active) return null;

	return (
		<>
			<div
				aria-hidden="true"
				className="pointer-events-none fixed inset-0 z-[2147483646] border-4 border-red-600"
			/>
			<div
				role="status"
				data-testid="support-session-banner"
				className="fixed left-1/2 top-0 z-[2147483647] flex -translate-x-1/2 items-center gap-3 rounded-b-lg bg-red-600 px-4 py-1.5 text-xs font-medium text-white shadow-lg"
			>
				<span className="font-semibold uppercase tracking-wide">
					Support session
				</span>
				<span>
					{session
						? `Viewing as ${session.target_email}`
						: "Viewing as a customer user"}
				</span>
				{session && (
					<span className="opacity-80">{session.admin_email}</span>
				)}
				{remaining !== null && (
					<span className="tabular-nums">{formatRemaining(remaining)}</span>
				)}
				<button
					type="button"
					onClick={exit}
					disabled={exiting}
					className="rounded bg-white px-2 py-0.5 font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
				>
					{exiting ? "Exiting…" : "Exit"}
				</button>
			</div>
		</>
	);
};

export default SupportSessionBanner;
